import { useEffect, useMemo, useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, fmtDateShort, parseAmountToCents } from '../format.js';

export default function Budgets({ state, lookups, generic, mutate, reload, loadFullHistory, showToast, onBack }) {
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  // Lazy-load full ledger so monthly rollups are accurate even when the
  // bootstrap's 50-most-recent doesn't cover all of this month's activity.
  useEffect(() => {
    if (state.allTransactions === null && loadFullHistory) {
      loadFullHistory().catch(() => {});
    }
  }, [state.allTransactions, loadFullHistory]);
  const txns = state.allTransactions ?? state.transactions;

  // Group budgets by month
  const months = useMemo(() => {
    const set = new Set(state.budgets.map(b => b.month));
    set.add(month); // ensure current always available
    return [...set].sort().reverse();
  }, [state.budgets, month]);

  const visible = state.budgets.filter(b => b.month === month);

  // Per-budget compute actuals for this month.
  // Sign convention: we store the SIGNED magnitude per category. Expense
  // categories naturally end up positive (we negate the negative amount_cents).
  // Income categories also end up positive (positive amount_cents stays
  // positive). Per-row UI treats both as "amount achieved toward target."
  const monthStart = month + '-01';
  const next = new Date(parseInt(month.slice(0,4),10), parseInt(month.slice(5,7),10), 0).toISOString().slice(0,10);

  const spendByCategory = useMemo(() => {
    const out = {};
    // Two-legged transfers (Savings, Transfer, Debt Payment) generate two
    // rows with the same category_id and same transfer_pair_id but opposite
    // signs. If we summed both, we'd double-count: a $300 PayPal payment
    // would show $600 against the Debt Payment budget.
    //
    // Strategy: for each transfer pair, keep only the DEBIT leg (negative
    // amount, the money leaving). Singletons (non-transfer transactions)
    // pass through unchanged. Income transactions are positive singletons
    // and are unaffected by this filter.
    const seenPairs = new Set();
    for (const t of txns) {
      if (t.occurred_on < monthStart || t.occurred_on > next) continue;
      if (t.transfer_pair_id) {
        if (seenPairs.has(t.transfer_pair_id)) continue;
        // Only count the debit leg (the side where money leaves the source
        // account). If we encounter the credit leg first, skip it; we'll
        // pick up the debit leg on a later iteration of the same pair.
        if (t.amount_cents > 0) continue;
        seenPairs.add(t.transfer_pair_id);
      }
      out[t.category_id] = (out[t.category_id] ?? 0) + Math.abs(t.amount_cents);
    }
    return out;
  }, [txns, monthStart, next]);

  // Group transactions by category for the per-budget detail view.
  // Same dedup rules as spendByCategory above.
  const txnsByCategory = useMemo(() => {
    const out = {};
    const seenPairs = new Set();
    for (const t of txns) {
      if (t.occurred_on < monthStart || t.occurred_on > next) continue;
      if (t.transfer_pair_id) {
        if (seenPairs.has(t.transfer_pair_id)) continue;
        if (t.amount_cents > 0) continue;
        seenPairs.add(t.transfer_pair_id);
      }
      const arr = out[t.category_id] ?? [];
      arr.push(t);
      out[t.category_id] = arr;
    }
    // Sort each category's list by date desc
    for (const cid of Object.keys(out)) {
      out[cid].sort((a, b) => a.occurred_on < b.occurred_on ? 1 : a.occurred_on > b.occurred_on ? -1 : 0);
    }
    return out;
  }, [txns, monthStart, next]);

  // Find the Income category id so we can exclude it from totals.
  // (Per-row stays — you still see Income progress alongside expenses.)
  const incomeCategoryIds = useMemo(() => {
    return state.categories
      .filter(c => /income/i.test(c.name))
      .map(c => c.id);
  }, [state.categories]);

  // Totals exclude Income so Target and Actual reflect EXPENSE budget health,
  // not a meaningless income+expense sum that mixes apples and oranges.
  const totalTarget = visible
    .filter(b => !incomeCategoryIds.includes(b.category_id))
    .reduce((s, b) => s + b.target_cents, 0);
  const totalActual = visible
    .filter(b => !incomeCategoryIds.includes(b.category_id))
    .reduce((s, b) => s + (spendByCategory[b.category_id] ?? 0), 0);

  return (
    <div className="screen">
      <div className="row" style={{ marginBottom: 16, gap: 12 }}>
        <button onClick={onBack} className="ghost">‹</button>
        <h1 style={{ margin: 0 }}>Budgets</h1>
      </div>

      <div className="row between" style={{ marginBottom: 12 }}>
        <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ flex: '0 0 auto', maxWidth: 180 }}>
          {months.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <button className="primary" onClick={() => setEditing('new')}>+ New</button>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row between">
          <div>
            <div className="muted tiny">Target (expenses)</div>
            <div className="amount" style={{ fontWeight: 600 }}>{fmtCents(totalTarget)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="muted tiny">Actual</div>
            <div className={'amount ' + (totalActual > totalTarget ? 'neg' : '')} style={{ fontWeight: 600 }}>
              {fmtCents(totalActual)}
            </div>
          </div>
        </div>
        <div className="muted tiny" style={{ marginTop: 6 }}>
          Income excluded from totals. Per-row income progress shown below.
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {visible.length === 0 ? (
          <div className="empty"><div className="icon">∅</div><div>No budgets for {month}</div></div>
        ) : visible.map(b => {
          const actual = spendByCategory[b.category_id] ?? 0;
          const pct = b.target_cents ? (actual / b.target_cents) * 100 : 0;
          const over = actual > b.target_cents;
          const tone = over ? 'neg' : pct >= 75 ? 'warn' : 'pos';
          return (
            <button key={b.id} onClick={() => setViewing(b)} className="list-item"
                    style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                             borderBottom: '1px solid var(--border2)', borderRadius: 0, cursor: 'pointer', flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              <div className="row between">
                <strong>{lookups.catName(b.category_id)}</strong>
                <span className={'amount ' + (over ? 'neg' : '')}>
                  {fmtCents(actual)} / {fmtCents(b.target_cents)}
                </span>
              </div>
              <div style={{ background: 'var(--bg2)', height: 4, borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, pct)}%`, height: '100%',
                              background: tone === 'neg' ? 'var(--red)' : tone === 'warn' ? 'var(--amber)' : 'var(--green)' }} />
              </div>
            </button>
          );
        })}
      </div>

      <BudgetDetail
        viewing={viewing}
        onClose={() => setViewing(null)}
        onEdit={() => { setEditing(viewing); setViewing(null); }}
        actual={viewing ? (spendByCategory[viewing.category_id] ?? 0) : 0}
        transactions={viewing ? (txnsByCategory[viewing.category_id] ?? []) : []}
        lookups={lookups}
      />

      <BudgetEditor editing={editing} onClose={() => setEditing(null)} state={state}
                    month={month} generic={generic} showToast={showToast} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BudgetDetail — drill-in modal showing the contributing transactions for a
// single category × month. Read-only list; "Edit budget" button switches to
// the editor modal. Transactions are ALREADY deduplicated for transfer pairs
// by the parent (txnsByCategory) so what we render here matches what's in
// the spendByCategory total exactly.
// ─────────────────────────────────────────────────────────────────────────────
function BudgetDetail({ viewing, onClose, onEdit, actual, transactions, lookups }) {
  if (!viewing) return null;
  const pct = viewing.target_cents ? (actual / viewing.target_cents) * 100 : 0;
  const over = actual > viewing.target_cents;
  const remaining = viewing.target_cents - actual;

  return (
    <Modal open={!!viewing} onClose={onClose} title={lookups.catName(viewing.category_id)}>
      <div className="muted tiny" style={{ marginBottom: 4 }}>
        {viewing.month}
      </div>
      <div className="row between" style={{ marginBottom: 12, alignItems: 'baseline' }}>
        <span className={'amount ' + (over ? 'neg' : '')} style={{ fontSize: 22, fontWeight: 600 }}>
          {fmtCents(actual)}
        </span>
        <span className="muted">of {fmtCents(viewing.target_cents)} target</span>
      </div>
      <div style={{ background: 'var(--bg2)', height: 6, borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%',
                      background: over ? 'var(--red)' : pct >= 75 ? 'var(--amber)' : 'var(--green)' }} />
      </div>
      <div className="muted tiny" style={{ marginBottom: 16 }}>
        {over
          ? `${fmtCents(actual - viewing.target_cents)} over`
          : `${fmtCents(remaining)} remaining`}
        {' · '}
        {Math.round(pct)}% used
      </div>

      <div className="muted tiny" style={{ marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Contributing transactions ({transactions.length})
      </div>
      {transactions.length === 0 ? (
        <div className="empty" style={{ padding: 16 }}>
          <div className="muted">No transactions in this category yet this month.</div>
        </div>
      ) : (
        <div style={{ maxHeight: '50vh', overflowY: 'auto', border: '1px solid var(--border2)', borderRadius: 6 }}>
          {transactions.map(t => (
            <div key={t.id} className="list-item" style={{
              borderBottom: '1px solid var(--border2)',
              padding: '10px 12px',
            }}>
              <div className="grow" style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.description || '(no description)'}
                </div>
                <div className="muted tiny" style={{ marginTop: 2 }}>
                  {fmtDateShort(t.occurred_on)}
                  {t.account_id && ` · ${lookups.acctName(t.account_id)}`}
                  {t.transfer_pair_id && ` · transfer`}
                </div>
              </div>
              <div className={'amount ' + (t.amount_cents >= 0 ? 'pos' : 'neg')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtCents(Math.abs(t.amount_cents))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="row between" style={{ marginTop: 16 }}>
        <button onClick={onClose}>Close</button>
        <button className="primary" onClick={onEdit}>Edit budget</button>
      </div>
    </Modal>
  );
}

function BudgetEditor({ editing, onClose, state, month, generic, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? { category_id: '', month, target_cents: 0 } : editing;

  return (
    <Modal open={!!editing} onClose={onClose} title={isNew ? 'New Budget' : 'Edit Budget'}>
      <BudgetForm key={isNew ? 'new' : editing.id} initial={initial} isNew={isNew}
                  state={state} generic={generic} showToast={showToast} onClose={onClose} />
    </Modal>
  );
}

function BudgetForm({ initial, isNew, state, generic, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    category_id: initial.category_id ?? '',
    month: initial.month ?? new Date().toISOString().slice(0, 7),
    targetInput: initial.target_cents ? (initial.target_cents / 100).toFixed(2) : '',
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    if (!form.category_id) return showToast({ kind: 'error', message: 'Category required.' });
    const target = parseAmountToCents(form.targetInput);
    if (target == null) return showToast({ kind: 'error', message: 'Target required.' });
    setSaving(true);
    try {
      const payload = { category_id: form.category_id, month: form.month, target_cents: target };
      if (isNew) await generic.budgets.create(payload);
      else await generic.budgets.update(initial.id, payload);
      showToast({ kind: 'success', message: 'Saved.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  async function remove() {
    const ok = await ask({ title: 'Delete budget?', message: 'This cannot be undone.', confirmLabel: 'Delete', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await generic.budgets.remove(initial.id);
      showToast({ kind: 'success', message: 'Deleted.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div className="field"><label>Category</label>
        <select value={form.category_id} onChange={(e) => update('category_id', e.target.value)} disabled={!isNew}>
          <option value="">Select…</option>
          {state.categories.filter(c => !c.archived).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select></div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field" style={{ flex: 1 }}><label>Month (YYYY-MM)</label>
          <input value={form.month} onChange={(e) => update('month', e.target.value)} pattern="\d{4}-\d{2}" /></div>
        <div className="field" style={{ flex: 1 }}><label>Target</label>
          <input inputMode="decimal" value={form.targetInput} onChange={(e) => update('targetInput', e.target.value)} /></div>
      </div>
      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? <button className="danger" onClick={remove} disabled={saving}>Delete</button> : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      </div>
    </>
  );
}
